import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Account, AccountStatus, AccountType } from '@monorepo/types';
import { useDashboard } from '../contexts';
import type {
  AccountTypeTag,
  StatusTag,
  TagData,
} from '../types/tag-data.types';
import { mapAssetTypeToTradingInstrument } from '../utils/asset-type.utils';

/**
 * Custom hook for generating account tag data with proper colors and labels
 *
 * @param account - Account data to generate tags for
 * @returns Memoized tag data object with tradingInstrument, accountType, and status
 */
export const useAccountTagData = (account: Account): TagData => {
  const { t } = useTranslation('dashboardHeader');
  const { assetType } = useDashboard();

  return useMemo(() => {
    /**
     * Gets the trading instrument configuration from assetType in context
     * Asset type is determined from account and can be overridden via QA query params
     */
    const getTradingInstrument = () => {
      return mapAssetTypeToTradingInstrument(assetType);
    };

    /**
     * Gets the account type tag configuration based on account type
     */
    const getAccountType = (): AccountTypeTag => {
      switch (account.type) {
        case AccountType.Evaluation:
          return {
            label: t('tags.accountType.evaluation'),
            color: 'blue',
          };
        case AccountType.Funded:
          return {
            label: t('tags.accountType.funded'),
            color: 'success',
          };
        case AccountType.Competition:
          return {
            label: t('tags.accountType.competition'),
            color: 'purple',
          };
        case AccountType.Demo:
          return {
            label: t('tags.accountType.demo'),
            color: 'textThird',
          };
        default:
          return {
            label: t('tags.accountType.evaluation'),
            color: 'blue',
          };
      }
    };

    /**
     * Determines the appropriate status tag based on account state and status
     * Inlined logic to avoid TypeScript circular dependency issues
     */
    const getStatus = (): StatusTag => {
      // TERMINATED STATE - Any termination condition
      if (
        account.state?.terminated ||
        account.state?.dailyLoss ||
        account.state?.expired
      ) {
        let label: string;
        if (account.state?.terminated) {
          label = t('tags.status.terminated');
        } else if (account.state?.dailyLoss) {
          label = t('tags.status.dailyLoss');
        } else {
          label = t('tags.status.expired');
        }

        return {
          label,
          color: 'error',
          statusDot: 'error',
        };
      }

      // TARGET REACHED STATE - Special case for target reached with disabled status
      if (account.state?.target && account.status === AccountStatus.Disabled) {
        return {
          label: t('tags.status.inactive'),
          color: 'yellow',
          statusDot: 'warning',
          tooltip: t('tags.status.tooltips.reachedTarget'),
        };
      }

      // INACTIVE STATE - Account disabled, paused, or waiting states
      if (
        account.status === AccountStatus.Disabled ||
        account.status === AccountStatus.PendingCreation ||
        account.status === AccountStatus.AwaitingAsyncCreation ||
        account.status === AccountStatus.PendingAsyncCreation ||
        account.status === AccountStatus.WaitingForRisk ||
        account.status === AccountStatus.WaitingForWithdraw ||
        account.status === AccountStatus.ReachedTarget ||
        account.state?.dailyPause
      ) {
        let tooltip: string;

        if (account.state?.dailyPause) {
          tooltip = t('tags.status.tooltips.dailyPause');
        } else if (account.status === AccountStatus.Disabled) {
          tooltip = account.disableReason || t('tags.status.tooltips.disabled');
        } else if (account.status === AccountStatus.PendingCreation) {
          tooltip = t('tags.status.tooltips.pendingCreation');
        } else if (account.status === AccountStatus.AwaitingAsyncCreation) {
          tooltip = t('tags.status.tooltips.awaitingAsyncCreation');
        } else if (account.status === AccountStatus.PendingAsyncCreation) {
          tooltip = t('tags.status.tooltips.pendingAsyncCreation');
        } else if (account.status === AccountStatus.WaitingForRisk) {
          tooltip = t('tags.status.tooltips.waitingForRisk');
        } else if (account.status === AccountStatus.WaitingForWithdraw) {
          tooltip = t('tags.status.tooltips.waitingForWithdraw');
        } else if (account.status === AccountStatus.ReachedTarget) {
          tooltip = t('tags.status.tooltips.default');
        } else {
          tooltip = t('tags.status.tooltips.default');
        }

        return {
          label: t('tags.status.inactive'),
          color: 'textThird',
          statusDot: 'inactive',
          tooltip,
        };
      }

      // ACTIVE STATE - Default active state or target reached
      return {
        label: t('tags.status.active'),
        color: 'success',
        statusDot: 'success',
      };
    };

    return {
      tradingInstrument: getTradingInstrument(),
      accountType: getAccountType(),
      status: getStatus(),
    };
  }, [
    account.type,
    account.status,
    account.state,
    account.disableReason,
    t,
    assetType,
  ]);
};
